import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type { AdminAlert, AdminSession } from "@ternary/contracts"
import { Alert, AppShell, Avatar, Button } from "@ternary/ui"
import { type ReactNode, useEffect } from "react"
import { Link, matchPath, useLocation, useNavigate } from "react-router-dom"
import { loadAdminAlerts, logoutAdmin } from "../api.js"
import { NavIcon } from "./nav-icons.js"

/**
 * Each item opens its section's first page, and is current on every page of the section: the
 * paths in `match` and the pages under them. Dashboard is current at /dashboard alone, since every
 * admin page sits under it.
 */
const navigation = [
  { icon: "dashboard", label: "Dashboard", path: "/dashboard", match: [] },
  {
    icon: "customers",
    label: "Customers",
    path: "/dashboard/customers",
    match: ["/dashboard/customers"],
  },
  {
    icon: "products",
    label: "Products",
    path: "/dashboard/products",
    match: ["/dashboard/products", "/dashboard/coupons", "/dashboard/rewards"],
  },
  { icon: "users", label: "Users", path: "/dashboard/users", match: ["/dashboard/users"] },
  {
    icon: "connections",
    label: "Connections",
    path: "/dashboard/discord",
    match: ["/dashboard/discord", "/dashboard/stripe", "/dashboard/pixels"],
  },
  {
    icon: "settings",
    label: "Settings",
    path: "/dashboard/settings",
    match: ["/dashboard/settings", "/dashboard/team", "/dashboard/account"],
  },
] as const

function isCurrent(item: (typeof navigation)[number], pathname: string) {
  return (
    matchPath(item.path, pathname) !== null ||
    item.match.some((path) => matchPath({ end: false, path }, pathname) !== null)
  )
}

/**
 * The page that fixes a warning, from its id (admin-api's alert ids; the contract has no field for
 * it): the product whose Discord role is gone or out of the bot's reach, Discord, or the account.
 * A product id is a Mongo ObjectId (24 hex digits, free and paid alike); anything else in its place
 * builds no path and falls back to Discord.
 */
function alertAction(alert: AdminAlert) {
  const product = /^discord-role(?:-gone)?-([a-f0-9]{24})-/.exec(alert.id)?.[1]
  if (product) {
    return {
      label: "Choose a role",
      to: `/dashboard/products/${encodeURIComponent(product)}/edit?step=access`,
    }
  }
  if (alert.id.startsWith("discord-")) {
    return { label: "Open Connections", to: "/dashboard/discord" }
  }
  // P4-F1: a first payment left waiting on the bank's check is finished from Account.
  if (alert.id === "first-payment-pending") {
    return { label: "Finish payment", to: "/dashboard/account" }
  }
  if (alert.id === "contract-expired" || alert.id === "latest-invoice-uncollectible") {
    return { label: "Open Account", to: "/dashboard/account" }
  }
  return null
}

/**
 * A warning in the warning tone with a link to its fix; a notice stays a neutral line. A warning
 * is a standing banner in the labelled "Admin alerts" region, not news: no alert role, so it is
 * not read out again at every sign-in.
 */
function AdminAlertItem({ alert }: { alert: AdminAlert }) {
  if (alert.kind === "update") {
    return (
      <a
        className="admin-alert"
        href="https://docs.ternarydev.com/ternary-developments/documentation/change-log"
        rel="noreferrer"
        target="_blank"
      >
        {alert.message}
      </a>
    )
  }
  if (alert.kind === "notice") {
    return <div className="admin-alert">{alert.message}</div>
  }
  const action = alertAction(alert)
  return (
    <Alert
      action={action ? <Link to={action.to}>{action.label}</Link> : undefined}
      role="none"
      tone="warning"
    >
      {alert.message}
    </Alert>
  )
}

export function AdminShell({ children, session }: { children: ReactNode; session: AdminSession }) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const queryClient = useQueryClient()
  const alerts = useQuery({
    queryFn: loadAdminAlerts,
    queryKey: ["admin-alerts"],
    refetchInterval: 60_000,
  })
  const logout = useMutation({
    mutationFn: logoutAdmin,
    onSettled() {
      queryClient.clear()
      queryClient.setQueryData(["admin-session"], { authenticated: false })
      navigate("/", { replace: true })
    },
  })

  useEffect(() => {
    document.title = `${session.organization.groupName} | Ternary`
  }, [session.organization.groupName])

  return (
    <AppShell
      brand={
        <>
          <Avatar name={session.organization.groupName} src={session.organization.groupLogo} />
          <span className="admin-brand">
            <span>{session.organization.groupName}</span>
            <span className="admin-brand-product">Ternary admin</span>
          </span>
        </>
      }
      footer={
        <>
          <span>{session.account.email}</span>
          <Button
            loading={logout.isPending}
            onClick={() => logout.mutate()}
            size="sm"
            variant="secondary"
          >
            Sign out
          </Button>
        </>
      }
      nav={navigation.map((item) => (
        <Link
          aria-current={isCurrent(item, pathname) ? "page" : undefined}
          key={item.path}
          to={item.path}
        >
          <NavIcon name={item.icon} />
          {item.label}
        </Link>
      ))}
      navLabel="Admin"
    >
      {alerts.data?.alerts.length ? (
        <section aria-label="Admin alerts" className="admin-alerts">
          {alerts.data.alerts.map((alert) => (
            <AdminAlertItem alert={alert} key={alert.id} />
          ))}
        </section>
      ) : null}
      {children}
    </AppShell>
  )
}
